/**
 * Shopping List → DELIVERABLES tab: the day's deliverables TOTALED across every
 * school, for batch-packing in advance (Yanping Ren / Masha, Ops: ten
 * schools ordering the same items, fridged the day before).
 *
 * Same day-section shell as the Recipes tab; inside each day the rows are
 * the standard kitchen `DeliverableRow`s under `MealtimeHeader`s, so the
 * packing view reads exactly like the per-school cards on /orders — just
 * summed. Rows open the same deliverable detail panel, scaled to the
 * MERGED quantity.
 */

import { Badge } from '@ordo/ui/badge'
import type { ReactNode } from 'react'

import { Badge as KitchenBadge } from '@/components/chefs/badge'
import { DeliverableRow } from '@/components/chefs/deliverable-row'
import { ListGroupHeader } from '@/components/chefs/list-group-header'
import { MealtimeHeader } from '@/components/chefs/mealtime-header'
import { SuppliesTable } from '@/components/chefs/supplies-table'
import type { DeliverablesDayGroup } from '@/lib/chefs/deliverables-by-day'
import { temperatureLabel } from '@/lib/chefs/format'
import type { MergedMilkRow } from '@/lib/chefs/milk'
import { deliverableTypeGroup, groupByTypeAndTemp, PACK_TYPE_LABELS } from '@/lib/chefs/pack-groups'
import { formatLongDate } from '@/lib/chefs/print-plan'
import { type CampusSupplyRow, visibleSupplyLines } from '@/lib/chefs/service-materials'
import type { Deliverable } from '@/lib/chefs/types'

export function ShoppingDeliverablesTab({
  dayGroups,
  supplyRows = [],
  ordersIncoming,
  onSelectDeliverable,
  selectedDeliverableId,
  emptyFallback,
}: {
  dayGroups: DeliverablesDayGroup[]
  /** Range totals; bags and utensils are pulled separately from the food containers. */
  supplyRows?: ReadonlyArray<CampusSupplyRow>
  /** The range's order window is still open — empty days read as pending. */
  ordersIncoming: boolean
  onSelectDeliverable: (deliverable: Deliverable) => void
  selectedDeliverableId?: string
  /** Rendered instead of day sections when the whole range has nothing. */
  emptyFallback: ReactNode
}) {
  const totalBlocks = dayGroups.reduce((sum, g) => sum + g.blocks.length + g.milk.length + g.forecastItems.length, 0)
  if (totalBlocks === 0 && visibleSupplyLines(supplyRows).length === 0) {
    return <>{emptyFallback}</>
  }
  return (
    <div className="flex flex-col gap-4 px-2 pb-4 lg:px-0">
      {dayGroups.map((group) => (
        <section key={group.iso} className="overflow-hidden rounded-2xl border border-border bg-background shadow-xs">
          <div className="flex items-baseline justify-between gap-3 border-b border-border px-4 py-3 lg:px-6">
            <h2 className="text-base font-medium text-foreground">{formatLongDate(group.date)}</h2>
            <span className="text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">{group.meals.toLocaleString()}</span> ordered meals ·{' '}
              {group.schoolCount} school{group.schoolCount === 1 ? '' : 's'} with orders
            </span>
          </div>
          {group.blocks.length === 0 && group.milk.length === 0 ? (
            <p className="px-4 py-4 text-sm italic text-muted-foreground lg:px-6">
              {ordersIncoming ? 'No confirmed packing quantities yet.' : 'Nothing to pack this day.'}
            </p>
          ) : (
            <div className="flex flex-col">
              <DeliverablesMilk rows={group.milk} />
              {group.blocks.map((block) => (
                <div key={block.mealtime}>
                  <MealtimeHeader mealtime={block.mealtime} count={block.meals} />
                  {/* Default grouping: TYPE (Mains include any package with a
                      main in it); within a type the rows are simply ORDERED
                      hot → chilled → ambient — the row's own temp icon carries
                      the signal, no temperature headings. */}
                  {groupByTypeAndTemp(
                    [...block.deliverables].sort((a, z) => a.name.localeCompare(z.name)),
                    deliverableTypeGroup,
                    (d) => d.temperature,
                  ).map((typeGroup) => (
                    <div key={typeGroup.group}>
                      <ListGroupHeader label={PACK_TYPE_LABELS[typeGroup.group]} count={typeGroup.items.length} />
                      <div className="flex flex-col divide-y divide-border">
                        {typeGroup.items.map((d) => (
                          <DeliverableRow
                            key={d.id}
                            deliverable={d}
                            selected={d.id === selectedDeliverableId}
                            onClick={() => onSelectDeliverable(d)}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
          {group.forecastItems.length > 0 ? (
            <div className="border-t border-border">
              <div className="px-4 py-3 lg:px-6">
                <h3 className="text-sm font-medium">
                  Upcoming menu items <Badge variant="warning">Forecast</Badge>
                </h3>
                <p className="mt-1 text-xs text-muted-foreground">
                  Estimated servings for this service date, not confirmed packing quantities. Packaging and delivery
                  dates follow placed orders.
                </p>
              </div>
              <div className="divide-y divide-border">
                {group.forecastItems.map((item) => (
                  <div key={item.key} className="flex items-center gap-3 px-4 py-2.5 text-sm lg:px-6">
                    <span className="w-28 shrink-0 text-xs italic text-muted-foreground">
                      {item.estimatedServings === null
                        ? 'No history yet'
                        : `~${Math.round(item.estimatedServings).toLocaleString()} servings`}
                      {item.missingHistory && item.estimatedServings !== null ? (
                        <span className="block">Partial estimate</span>
                      ) : null}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p>{item.name}</p>
                      <p className="text-xs text-muted-foreground">{item.schoolNames.join(' · ')}</p>
                    </div>
                    <Badge variant="warning">Forecast</Badge>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </section>
      ))}
      <SuppliesTable rows={supplyRows} />
    </div>
  )
}

/**
 * The day's milk on top of the mealtimes, summed across schools (PROD-1502 moved
 * milk out of the mealtime rows onto each campus's milk card; the printed
 * Deliverables page carries the same block).
 */
function DeliverablesMilk({ rows }: { rows: MergedMilkRow[] }) {
  if (rows.length === 0) {
    return null
  }
  return (
    <div data-testid="deliverables-milk">
      <ListGroupHeader label="Milk" count={rows.length} />
      <div className="flex flex-col divide-y divide-border">
        {rows.map((row) => {
          const detail = [row.packaging, row.servingSize ? `${row.servingSize} per serving` : null]
            .filter(Boolean)
            .join(' → ')
          return (
            <div key={row.key} className="flex items-center gap-3 px-4 py-2.5 text-sm lg:px-6">
              <span className="w-14 shrink-0 text-xs font-medium text-muted-foreground">
                {row.qty.toLocaleString()}x
              </span>
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="truncate text-foreground">{row.name}</span>
                {detail ? <span className="text-xs text-muted-foreground">{detail}</span> : null}
              </span>
              <KitchenBadge variant={row.temperature} className="px-2">
                {temperatureLabel(row.temperature)}
              </KitchenBadge>
            </div>
          )
        })}
      </div>
    </div>
  )
}
